import { AGENT_IDS, DATA_CATALOG_API_TOOL_IDS } from '@/app/data-catalog/_common/constants'
import { useApiRequest } from '@/lib/http-utils'
import { UndefinedInitialDataOptions, useQuery } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'

export type SensitivityTagValue = 'Public' | 'Internal' | 'Confidential' | 'Restricted'

export type FrameworkReference = {
  reference: string
  summary: string
}

export type FrameworkCitation = {
  framework: string
  references: FrameworkReference[]
}

export type TagItem = {
  tag: string
  description?: string
  justification?: string
  framework_citations?: FrameworkCitation[]
  frameworkCitations?: FrameworkCitation[]
}

export type DataQualityTagItem = {
  tag: string
  description?: string
  justification?: string
  framework_citations?: FrameworkCitation[]
  frameworkCitations?: FrameworkCitation[]
  data_quality_rule?: string
  dq_sql_script?: string
  execution_result?: string
  execution_output?: Record<string, any>
}

export type SensitivityTagItem = {
  tag: SensitivityTagValue
  description?: string
  justification?: string
  framework_citations?: FrameworkCitation[]
  frameworkCitations?: FrameworkCitation[]
}

export type TagsStructure = {
  PII_DG?: TagItem[]
  DATA_QUALITY_TAGS?: DataQualityTagItem[]
  SENSITIVITY_TAGS?: SensitivityTagItem[]
  piiDg?: TagItem[]
  dataQualityTags?: DataQualityTagItem[]
  sensitivityTags?: SensitivityTagItem[]
}

export type ColumnDetail = {
  column_name: string
  column_description: string
  data_type: string
  distinct_count: number
  null_count: number
  min_value: number | null
  max_value: number | null
  sample_values: string[]
  categories: string[]
  business_definition: string
  business_rules: string
  tags: TagsStructure
}

export type TableDetails = {
  table_name: string
  table_domain: string
  table_description: string
  table_owners: string[]
  table_version: number
  row_count: number
  column_count: number
  all_columns: string[]
  tags: TagsStructure
  is_dq_tagged: number | null
  is_pii_tagged: number | null
  is_sensitivity_tagged: number | null
  columns: ColumnDetail[]
}

export type TableDetailsAPIResponse = {
  service_name: string
  database_source: string
  database_name: string
  schema_name: string
  schema_description: string
  schema_owners: string[]
  tables: TableDetails[]
  error?: string
}

export type TableDetailsQueryParams = {
  service_name: string
  database_name: string
  schema_name: string
  table_name: string
  tableVersion?: string
  assetType?: string
}

export function useTableDetailsQuery(
  params: TableDetailsQueryParams,
  queryOptions: Omit<
    UndefinedInitialDataOptions<TableDetailsAPIResponse>,
    'queryKey' | 'queryFn'
  > = {},
) {
  const agentId = AGENT_IDS.DATA_CATALOG_AGENT
  const { apiRequest, domainId } = useApiRequest()

  return useQuery<TableDetailsAPIResponse>({
    queryKey: [
      'table-details',
      params.service_name,
      params.database_name,
      params.schema_name,
      params.table_name,
      params.tableVersion,
    ],
    queryFn: async () => {
      const basePath = taskManagerApiPaths.agentById({ domainId, agentId })

      const response = await apiRequest({
        path: basePath,
        queryParams: {
          tool_id: DATA_CATALOG_API_TOOL_IDS.FETCH_TABLE_DETAILS,
        },
        options: {
          method: 'POST',
          body: JSON.stringify({
            catalogSelector: {
              service_name: params.service_name,
              database_name: params.database_name,
              schema_name: params.schema_name,
              table_name: params.table_name,
              version: params.tableVersion?.toString(),
            },
          }),
        },
      })
      if (!response.ok) {
        throw new Error('Error fetching tables details')
      }

      const json = (await response.json()) as { output: TableDetailsAPIResponse }
      return json.output
    },
    ...queryOptions,
  })
}
